import { FieldValues, UseFormSetValue } from "react-hook-form";
import { SalesCreditDetail } from "../../api/services/program/models";
import {
  ProjectAddFeeOrExpenseRequestPayload,
  SaveOrEditProjectPrimaryDetailsPayload,
  ViewProjectFeeOrExpenseDetails,
} from "../../api/services/project/model";
import { OptionsDataType } from "../../components";
import { CURRENCY_FORMAT_MAP } from "../../components/textFieldCurrency/CurrencyUtils";
import { KeyValue } from "../../helpers/types/common";
import {
  checkWithLowerCase,
  convertToUTCFormat,
  formatDate,
  getFormattedAmount,
  getTaxPayloadFromTaxEntries,
  getValueWith2DecimalDigits,
  isNotEmpty,
} from "../../helpers/utils";
import { FacilitatorDetails } from "./components/createProjectTabs/tabs/project/components/facilitator/model";
import {
  ADD_PROJECT_FEE_AND_EXPENSE_TYPES,
  ProjectAddOrEditExpenseOrFeeFormKey,
  ProjectTabFormType,
} from "./modelsAndSchemas/Models";

export const FIXED_PROJECT_SALES_CREDIT_PERCENTAGE: OptionsDataType[] = [
  "0",
  "30",
  "33",
  "34",
  "50",
  "67",
  "70",
  "100",
].map((value) => ({
  label: value.toString(),
  value: value,
}));

export const MAP_33_34_67 = ["33", "34", "67"].map((value) => ({
  label: value.toString(),
  value: value,
}));

export const VALUE_MAPPER: Record<string, string[]> = {
  "0": ["100"],
  "100": ["0"],
  "30": ["70"],
  "70": ["30"],
  "67": ["33"],
  "33": ["33", "34", "67"],
  "34": ["33"],
  "50": ["50"],
};

export const MAX_PROJECT_SALES_CREDIT_ENTRIES = 3;

const getSalesCreditDetailsPayload = (
  salesCreditDetails?: SalesCreditDetail[],
) => {
  if (isNotEmpty(salesCreditDetails)) {
    return salesCreditDetails.map((item) => {
      return {
        localClientRelationshipOwnerId: item.localClientRelationshipOwnerId,
        percentage: item.percentage,
      };
    });
  }
  return [];
};

const getSaveFacilitatorPayload = (
  userCountryDateFormat: string,
  projectFacilitator?: FacilitatorDetails[],
) => {
  if (isNotEmpty(projectFacilitator)) {
    return projectFacilitator.map((item) => {
      return {
        functionName: item.functionId,
        facilitatorId: item.facilitatorId,
        assignedDate: convertToUTCFormat(
          item.assignmentDate,
          userCountryDateFormat,
        ),
      };
    });
  }
  return [];
};

export const getProjectPrimaryDetailsPayload = ({
  formValues,
  projectId,
  userCountryDateFormat,
}: {
  formValues: ProjectTabFormType;
  projectId: string;
  userCountryDateFormat: string;
}): SaveOrEditProjectPrimaryDetailsPayload => {
  const {
    billingContact: billingContactId,
    hrContact: hrContactId,
    clientRelationShipOwner: localClientRelationshipOwnerId,
    projectManager: projectManagerId,
    projectCoordinator: projectCoordinatorId,
    salesCreditDetails,
    projectFacilitator,
    facilitatorAssignDate,
  } = formValues;

  return {
    id: projectId,
    officeAndContact: {
      localClientRelationshipOwnerId: localClientRelationshipOwnerId ?? "",
      billingContactId: billingContactId ?? "",
      hrContactId: hrContactId ?? "",
      projectManagerId: projectManagerId ?? [],
      projectCoordinatorId: projectCoordinatorId ?? [],
    },
    salesCredit: getSalesCreditDetailsPayload(salesCreditDetails),
    projectFacilitator: {
      assignedDate: facilitatorAssignDate
        ? convertToUTCFormat(facilitatorAssignDate, userCountryDateFormat)
        : "",
      projectFacilitatorTypeAssignments: getSaveFacilitatorPayload(
        userCountryDateFormat,
        projectFacilitator,
      ),
    },
  };
};

export const setFormProjectExpenseData = (
  projectExpenseDetails: ViewProjectFeeOrExpenseDetails,
  setValue: UseFormSetValue<FieldValues>,
  projectBillingCurrencyName: string,
  userCountryDateFormat?: string,
) => {
  const {
    type,
    taxDetails,
    expenseDetails: {
      feeName,
      transactionDate,
      feeType,
      exchangeRate,
      feeAmount,
      comments,
      expenseCategory,
      incurredBy,
      unitRate,
      quantity,
      expenseType,
      feeCurrency: { name: feeCurrencyName, id: feeCurrencyId },
      billingAmount,
    },
  } = projectExpenseDetails;
  const feeAmountWithFormatValue = `${getFormattedAmount(
    feeAmount,
    CURRENCY_FORMAT_MAP[feeCurrencyName]?.locale,
  )}`;
  const billingAmountWithFormatValue = `${getFormattedAmount(
    billingAmount,
    CURRENCY_FORMAT_MAP[projectBillingCurrencyName]?.locale,
  )}`;
  setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectType, type);
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseName,
    feeName,
  );
  if (transactionDate) {
    const formattedDate = formatDate(transactionDate, userCountryDateFormat);
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectTransactionDate,
      formattedDate,
    );
  }
  feeType &&
    setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectFeeTypeId, feeType.id);
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyId,
    feeCurrencyId,
  );
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseCurrencyName,
    feeCurrencyName,
  );
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmount,
    feeAmount,
  );
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectFeeOrExpenseAmountWithFormat,
    feeAmountWithFormatValue,
  );
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmount,
    billingAmount,
  );
  setValue(
    ProjectAddOrEditExpenseOrFeeFormKey.projectBillingAmountWithFormat,
    billingAmountWithFormatValue,
  );
  setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectIsRateConverted, true);
  if (exchangeRate) {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectBillingCurrencyExchangeRate,
      exchangeRate,
    );
  }

  setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectComments, comments);
  if (taxDetails) {
    setValue(ProjectAddOrEditExpenseOrFeeFormKey.projectTaxEntries, taxDetails);
  }

  if (expenseCategory) {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseCategoryId,
      expenseCategory.id,
    );
  }
  if (expenseType) {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseTypeId,
      expenseType.id,
    );
  }
  if (incurredBy) {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseIncurredById,
      incurredBy.id,
    );
  }
  if (unitRate) {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseUnitPrice,
      unitRate,
    );
  }
  if (quantity) {
    setValue(
      ProjectAddOrEditExpenseOrFeeFormKey.projectExpenseQuantity,
      quantity,
    );
  }
};

export const getProjectAddFeesOrExpensePayload = (
  formValue: KeyValue,
  projectId: string,
  userCountryDateFormat?: string,
): ProjectAddFeeOrExpenseRequestPayload => {
  const {
    type,
    transactionDate,
    expenseCategoryId,
    feeTypeId,
    feeOrExpenseName,
    expenseTypeId,
    expenseQuantity,
    expenseUnitPrice,
    feeOrExpenseCurrencyId,
    feeOrExpenseAmount,
    billingCurrencyExchangeRate,
    comments,
    billingAmount,
    expenseIncurredById,
    taxEntries,
  } = formValue;

  return {
    type,
    expenseDetails: {
      projectId,
      transactionDate:
        transactionDate &&
        convertToUTCFormat(transactionDate, userCountryDateFormat),
      feeTypeId,
      feeName: feeOrExpenseName,
      expenseCategoryId,
      expenseTypeId,
      feeCurrencyId: feeOrExpenseCurrencyId,
      unitRate: expenseUnitPrice && Number(expenseUnitPrice),
      quantity: expenseQuantity && Number(expenseQuantity),
      feeAmount: Number(feeOrExpenseAmount),
      billingAmount: Number(getValueWith2DecimalDigits(billingAmount)),
      comments: comments,
      exchangeRate:
        billingCurrencyExchangeRate && Number(billingCurrencyExchangeRate),
      incurredById: expenseIncurredById,
    },
    taxDetails: taxEntries && getTaxPayloadFromTaxEntries(taxEntries),
  };
};

export const isProjectFeeType = (type: string) => {
  return checkWithLowerCase(
    ADD_PROJECT_FEE_AND_EXPENSE_TYPES.PROJECT_FEE,
    type,
  );
};
